import { useMemo } from "react";

const ROLES = ["P", "D", "C", "A"];
const COLORS = {
  P: "#F59E0B",   // amber for portieri
  D: "#38BDF8",   // sky for difensori
  C: "#10B981",   // emerald for centrocampisti
  A: "#F472B6",   // pink for attaccanti
};

/**
 * Horizontal stacked bar: shows how much a team spent per role (P/D/C/A).
 * Falls back to a flat "nessuna spesa ancora" line when roster is empty.
 */
export default function RosterSpendChart({ players = [], testIdPrefix = "" }) {
  const { byRole, total } = useMemo(() => {
    const sums = { P: 0, D: 0, C: 0, A: 0 };
    for (const p of players) {
      if (ROLES.includes(p.role)) sums[p.role] += Number(p.price || 0);
    }
    const t = sums.P + sums.D + sums.C + sums.A;
    return { byRole: sums, total: t };
  }, [players]);

  if (total <= 0) {
    return (
      <div className="text-[10px] text-slate-500 italic mt-1" data-testid={`${testIdPrefix}empty`}>
        Nessuna spesa ancora
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid={`${testIdPrefix}chart`}>
      <div className="flex h-2 w-full rounded-full overflow-hidden bg-slate-800/60 ring-1 ring-white/5">
        {ROLES.map((r) => {
          const v = byRole[r];
          if (v <= 0) return null;
          const w = (v / total) * 100;
          return (
            <div
              key={r}
              data-testid={`${testIdPrefix}seg-${r}`}
              className="h-full first:rounded-l-full last:rounded-r-full"
              style={{ width: `${w}%`, background: COLORS[r] }}
              title={`${r}: ${v} FM`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 text-[9px] font-mono">
        {ROLES.map((r) => (
          <span key={r} className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}legend-${r}`}>
            <span className="w-1.5 h-1.5 rounded-sm" style={{ background: COLORS[r] }} />
            <span className="text-slate-400">{r}</span>
            <span className="text-slate-200 font-bold">{byRole[r]}</span>
          </span>
        ))}
        <span className="ml-auto text-slate-500">tot <span className="text-emerald-400 font-bold">{total}</span></span>
      </div>
    </div>
  );
}
